import {
  faCircle,
  faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { StepSidebar } from "src/cases/common/StepSidebar";
import { createStepErrorNavigation } from "src/cases/common/stepErrorNavigation";
import {
  CREATE_CASE_STEPS,
  CreateCaseFormValues,
  CreateCaseStepId,
  useCurrentCreateCaseStep,
} from "src/cases/createCase/createCaseSteps";
import { Icon, IconVariant } from "src/design-system/Icon";

const { stepHasErrors } = createStepErrorNavigation<CreateCaseStepId>([
  { pathPrefix: "source", stepId: "context" },
  { pathPrefix: "comment", stepId: "context" },
  { pathPrefix: "file_category_files", stepId: "context" },
  { pathPrefix: "case_properties", stepId: "properties" },
  { pathPrefix: "status", stepId: "case-routing" },
  { pathPrefix: "sub_status", stepId: "case-routing" },
  { pathPrefix: "assignee_id", stepId: "case-routing" },
]);

export const CreateCaseSidebar: React.FC = () => {
  const [currentStepId, setCurrentStep] = useCurrentCreateCaseStep();
  const { formState } = useFormContext<CreateCaseFormValues>();

  return (
    <StepSidebar
      ariaLabel="Create case form steps"
      currentStepId={currentStepId}
      steps={CREATE_CASE_STEPS}
    >
      {(step, index) => {
        const hasErrors = stepHasErrors(
          formState.errors,
          step.id as CreateCaseStepId,
        );

        return (
          <StepButton
            active={currentStepId === step.id}
            hasErrors={hasErrors}
            stepNumber={index + 1}
            totalSteps={CREATE_CASE_STEPS.length}
            onClick={() => setCurrentStep(step.id)}
          >
            {step.title}
          </StepButton>
        );
      }}
    </StepSidebar>
  );
};

const StepButton: React.FC<{
  active: boolean;
  hasErrors: boolean;
  onClick: () => void;
  children: string;
  stepNumber: number;
  totalSteps: number;
}> = ({ active, hasErrors, onClick, children, stepNumber, totalSteps }) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const getIcon = () => {
    if (hasErrors) return faCircleExclamation;
    return faCircle;
  };

  const getIconVariant = (): IconVariant => {
    if (hasErrors) return "error";
    return "default";
  };

  return (
    <button
      aria-current={active ? "step" : undefined}
      aria-label={`Step ${stepNumber} of ${totalSteps}: ${children}${hasErrors ? " (has errors)" : ""}`}
      className={twMerge(
        "z-10 flex h-6 w-full items-center gap-x-2 rounded-lg px-2 transition-all duration-300",
        !active && "cursor-pointer hover:bg-gray-50",
        "font-inter-medium-13px",
      )}
      type="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Icon
        aria-hidden="true"
        icon={getIcon()}
        size="xs"
        variant={getIconVariant()}
      />
      {children}
    </button>
  );
};
